import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmt } from '../lib/format';
import { ArrowLeft, CheckCircle, Info, ArrowsHorizontal, Storefront, Globe } from '@phosphor-icons/react';

export default function NewOrder() {
  const navigate = useNavigate();
  const { user, rate } = useAuth();

  // Order type: offline (default, daily) or online (rare, USDT direct)
  const [orderType, setOrderType] = useState('offline');

  // Offline fields
  const [currency, setCurrency] = useState('MYR');
  const [sellRate, setSellRate] = useState('');
  const [feePercent, setFeePercent] = useState('0');

  // Shared fields
  const [email, setEmail] = useState('');
  const [amount, setAmount] = useState('');

  // Online fields
  const [creditedAmount, setCreditedAmount] = useState('');
  const todayStr = new Date().toISOString().slice(0, 10);
  const [orderDate, setOrderDate] = useState(todayStr);

  // Payment status (for both online and offline)
  const [paid, setPaid] = useState(true); // true = 已收款 (default), false = 待收款

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const suggestTimer = useRef(null);

  const [step, setStep] = useState(1);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderId, setOrderId] = useState('');

  const isAdmin = user?.role === 'admin';

  const onEmailChange = (v) => {
    setEmail(v);
    clearTimeout(suggestTimer.current);
    if (v.length >= 1) {
      suggestTimer.current = setTimeout(async () => {
        try {
          const r = await api.get('/customers/suggest', { params: { q: v } });
          setSuggestions(r.data);
          setShowSuggest(true);
        } catch (err) {
          console.warn('Customer suggest failed:', err);
        }
      }, 200);
    } else {
      setSuggestions([]);
      setShowSuggest(false);
    }
  };

  const buildBody = (forPreview) => {
    if (orderType === 'online') {
      const body = {
        order_type: 'online',
        currency: 'USDT',
        amount: parseFloat(amount),
        credited_amount: parseFloat(creditedAmount),
        fee_percent: 0,
      };
      if (!forPreview) {
        body.customer_email = email.trim().toLowerCase();
        if (orderDate) body.order_date = orderDate;
        body.payment_status = paid ? 'paid' : 'owed';
      }
      return body;
    }
    // offline
    const body = {
      order_type: 'offline',
      currency,
      amount: parseFloat(amount),
      fee_percent: parseFloat(feePercent || 0),
    };
    if (currency === 'MYR') body.sell_rate = parseFloat(sellRate);
    if (!forPreview) {
      body.customer_email = email.trim().toLowerCase();
      body.payment_status = paid ? 'paid' : 'owed';
    }
    return body;
  };

  const doPreview = async (e) => {
    e?.preventDefault();
    setError('');
    if (orderType === 'online') {
      const a = parseFloat(amount);
      const c = parseFloat(creditedAmount);
      if (!a || !c) {
        setError('请填写客户充值金额和实际到账金额');
        return;
      }
      if (c > a) {
        setError('实际到账金额不能大于客户充值金额');
        return;
      }
    }
    setLoading(true);
    try {
      const r = await api.post('/orders/preview', buildBody(true));
      setPreview(r.data);
      setStep(2);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submit = async () => {
    setError('');
    setLoading(true);
    try {
      const r = await api.post('/orders', buildBody(false));
      setOrderId(r.data.id);
      setStep(3);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const reset = () => {
    setEmail(''); setAmount(''); setSellRate(''); setFeePercent('0');
    setCreditedAmount(''); setOrderDate(todayStr); setPaid(true);
    setStep(1); setPreview(null); setOrderId(''); setError('');
  };

  return (
    <div className="max-w-2xl mx-auto fade-in" data-testid="new-order-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white tracking-tight">录入订单</h1>
        <p className="text-sm text-slate-400 mt-1">填写客户充值信息</p>
      </div>

      {/* Order type toggle */}
      {step === 1 && (
        <div className="card-base p-2 mb-5 grid grid-cols-2 gap-2" data-testid="order-type-toggle">
          <button
            type="button"
            data-testid="type-offline"
            onClick={() => setOrderType('offline')}
            className={`py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              orderType === 'offline'
                ? 'bg-indigo-600 text-white shadow shadow-indigo-900/40'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Storefront size={18} weight="duotone" /> 线下充值
          </button>
          <button
            type="button"
            data-testid="type-online"
            onClick={() => setOrderType('online')}
            className={`py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              orderType === 'online'
                ? 'bg-sky-600 text-white shadow shadow-sky-900/40'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Globe size={18} weight="duotone" /> 线上充值
          </button>
        </div>
      )}

      {/* Rate banner (offline only) */}
      {step === 1 && orderType === 'offline' && rate && (
        <div className="card-base p-4 mb-5 border-indigo-500/20 flex items-center justify-between" style={{
          background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(23,33,54,1))'
        }}>
          <div className="flex items-center gap-3">
            <ArrowsHorizontal size={20} weight="duotone" className="text-indigo-400" />
            <div>
              <div className="label-tiny">今日成本汇率</div>
              <div className="font-num text-xl font-semibold text-white">
                1 USDT = <span className="text-indigo-300">{rate.cost_rate}</span> MYR
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500 hidden sm:block">
            <div>由 {rate.updated_by} 设置</div>
            {!isAdmin && <div className="text-amber-400/70 mt-0.5">不可修改</div>}
          </div>
        </div>
      )}

      {/* Online info banner */}
      {step === 1 && orderType === 'online' && (
        <div className="card-base p-4 mb-5 border-sky-500/20 flex items-start gap-3" style={{
          background: 'linear-gradient(135deg, rgba(14,165,233,0.08), rgba(23,33,54,1))'
        }}>
          <Globe size={20} weight="duotone" className="text-sky-400 mt-0.5" />
          <div className="text-sm text-slate-300">
            <div className="font-medium text-sky-300 mb-0.5">线上 USDT 充值</div>
            <div className="text-xs text-slate-400">客户直接转 USDT 给我们,扣除平台/Gas 费后到账。利润 = 充值金额 − 实际到账金额</div>
          </div>
        </div>
      )}

      {/* Steps indicator */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map((n, i) => (
          <React.Fragment key={n}>
            <div className={`flex items-center gap-2 ${step >= n ? 'text-indigo-300' : 'text-slate-600'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${
                step >= n ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-500'
              }`}>{n}</div>
              <span className="text-xs font-medium hidden sm:inline">
                {n === 1 ? '填写' : n === 2 ? '预览' : '完成'}
              </span>
            </div>
            {i < 2 && <div className={`flex-1 h-px ${step > n ? 'bg-indigo-600' : 'bg-slate-800'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Fill */}
      {step === 1 && (
        <form onSubmit={doPreview} className="card-base p-5 sm:p-6 space-y-5" data-testid="order-form-step1">
          {/* Currency (offline only) */}
          {orderType === 'offline' && (
            <div>
              <label className="label-tiny block mb-2">付款币种</label>
              <div className="grid grid-cols-2 gap-2">
                {['MYR', 'USDT'].map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`currency-${c}`}
                    onClick={() => setCurrency(c)}
                    className={`py-3 rounded-lg border font-medium transition-all ${
                      currency === c
                        ? 'bg-indigo-600 border-indigo-500 text-white shadow shadow-indigo-900/40'
                        : 'bg-slate-900/50 border-slate-700 text-slate-300 hover:border-slate-600'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Customer Email */}
          <div className="relative">
            <label className="label-tiny block mb-2">客户 Email</label>
            <input
              data-testid="order-email"
              type="email"
              required
              className="input-base"
              placeholder="forwork…"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
            />
            {showSuggest && suggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden">
                {suggestions.map((s) => (
                  <button
                    type="button"
                    key={s.email}
                    onClick={() => { setEmail(s.email); setShowSuggest(false); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-indigo-600/15 text-sm text-slate-200 border-b border-slate-800 last:border-0 flex justify-between"
                  >
                    <span className="truncate">{s.email}</span>
                    <span className="text-xs text-slate-500 font-num ml-2">{s.order_count} 笔</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {orderType === 'online' ? (
            <>
              {/* Date picker (online only) */}
              <div>
                <label className="label-tiny block mb-2">充值日期</label>
                <input
                  data-testid="order-date"
                  type="date"
                  required
                  className="input-base font-num"
                  max={todayStr}
                  value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1.5">默认今天,可选过去日期</p>
              </div>

              {/* Customer top-up amount */}
              <div>
                <label className="label-tiny block mb-2">客户充值金额 (USDT)</label>
                <input
                  data-testid="order-topup-amount"
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  className="input-base font-num text-lg"
                  placeholder="1000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1.5">客户从他的钱包转过来的金额</p>
              </div>

              {/* Credited amount */}
              <div>
                <label className="label-tiny block mb-2">实际到账金额 (USDT)</label>
                <input
                  data-testid="order-credited-amount"
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  className="input-base font-num text-lg"
                  placeholder="996"
                  value={creditedAmount}
                  onChange={(e) => setCreditedAmount(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1.5">扣除平台/Gas 费后真正到账的金额</p>
              </div>

              {/* Live profit preview */}
              {amount && creditedAmount && parseFloat(amount) >= parseFloat(creditedAmount) && (
                <div className="rounded-lg bg-sky-500/10 border border-sky-500/20 px-4 py-3 flex items-center justify-between">
                  <span className="text-sm text-sky-200">预计利润</span>
                  <span className="font-num text-lg font-semibold text-emerald-300">
                    +{fmt(parseFloat(amount) - parseFloat(creditedAmount))} USDT
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Amount */}
              <div>
                <label className="label-tiny block mb-2">充值金额 ({currency})</label>
                <input
                  data-testid="order-amount"
                  type="number"
                  required
                  step="0.01"
                  min="0"
                  className="input-base font-num text-lg"
                  placeholder={currency === 'MYR' ? '10000' : '1000'}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>

              {/* Sell rate (MYR only) */}
              {currency === 'MYR' && (
                <div>
                  <label className="label-tiny block mb-2">卖出汇率 (MYR → USDT)</label>
                  <input
                    data-testid="order-sell-rate"
                    type="number"
                    required
                    step="0.0001"
                    min="0"
                    className="input-base font-num text-lg"
                    placeholder="4.10"
                    value={sellRate}
                    onChange={(e) => setSellRate(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1.5">
                    成本 {rate?.cost_rate} → 你给客户的卖出价
                  </p>
                </div>
              )}

              {/* Fee */}
              <div>
                <label className="label-tiny block mb-2">手续费 (%)</label>
                <input
                  data-testid="order-fee"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="input-base font-num text-lg"
                  placeholder="0 (无手续费) / 1 / 1.8 / 2"
                  value={feePercent}
                  onChange={(e) => setFeePercent(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1.5">VIP 客户可填 0</p>
              </div>
            </>
          )}

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
              {error}
            </div>
          )}

          {/* Payment status toggle */}
          <div className={`rounded-lg border p-3 transition-colors ${
            paid ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-rose-500/30 bg-rose-500/10'
          }`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={paid}
                onChange={(e) => setPaid(e.target.checked)}
                data-testid="paid-checkbox"
                className="mt-1 w-4 h-4 accent-emerald-500"
              />
              <div className="flex-1">
                <div className={`text-sm font-medium ${paid ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {paid ? '✅ 客户已付款' : '🔴 客户暂未付款(待收款)'}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {paid
                    ? '默认勾选:客户已经把钱给你了'
                    : '不勾选:客户还欠款,订单会标记为「待收款」,提醒去收钱'}
                </div>
              </div>
            </label>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-3" data-testid="preview-btn">
            {loading ? '计算中…' : '下一步 → 预览'}
          </button>
        </form>
      )}

      {/* Step 2: Preview */}
      {step === 2 && preview && (
        <div className="card-base p-5 sm:p-6" data-testid="order-preview">
          <div className="flex items-center gap-2 text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5 mb-5 text-sm">
            <Info size={16} weight="duotone" />
            <span>请仔细核对,提交后只能删除不能修改</span>
          </div>

          <div className="space-y-3 mb-5">
            <Row label="类型" value={orderType === 'online' ? '🌐 线上充值' : '⛺ 线下充值'} />
            <Row label="客户" value={email} />
            <Row label="付款状态" value={paid ? '✅ 已付款' : '🔴 待收款'} highlight={!paid} />
            {orderType === 'online' ? (
              <>
                <Row label="充值日期" value={orderDate} mono />
                <Row label="客户充值金额" value={`${fmt(amount)} USDT`} mono />
                <Row label="实际到账金额" value={`${fmt(creditedAmount)} USDT`} mono />
              </>
            ) : (
              <>
                <Row label="币种" value={currency} />
                <Row label="金额" value={`${fmt(amount)} ${currency}`} />
                {currency === 'MYR' && (
                  <>
                    <Row label="成本汇率" value={preview.cost_rate} mono />
                    <Row label="卖出汇率" value={preview.sell_rate} mono />
                  </>
                )}
                <Row label="手续费%" value={`${feePercent}%`} />
              </>
            )}
          </div>

          <div className="bg-gradient-to-br from-indigo-500/10 to-violet-500/5 border border-indigo-500/20 rounded-xl p-5 space-y-3">
            <div className="label-tiny mb-2">系统计算结果</div>
            {orderType === 'online' ? (
              <>
                <Row label="客户转入 Gross" value={`${fmt(preview.gross_usdt)} USDT`} mono />
                <Row label="💎 实际到账 Net" value={`${fmt(preview.net_usdt)} USDT`} mono big highlight />
                {isAdmin && (
                  <>
                    <div className="h-px bg-slate-700/50 my-2" />
                    <Row label="✨ 本单利润" value={`+${fmt(preview.total_profit_usdt)} USDT`} mono profit big />
                  </>
                )}
              </>
            ) : (
              <>
                {currency === 'MYR' && (
                  <>
                    <Row label="手续费金额" value={`${fmt(preview.fee_amount_myr)} MYR`} mono />
                    <Row label="扣费后金额" value={`${fmt(preview.net_amount_myr)} MYR`} mono />
                  </>
                )}
                {currency === 'USDT' && (
                  <Row label="手续费金额" value={`${fmt(preview.fee_amount_usdt)} USDT`} mono />
                )}
                <div className="h-px bg-slate-700/50 my-2" />
                <Row label="Gross" value={`${fmt(preview.gross_usdt)} USDT`} mono />
                <Row label="💎 客户实收 Net" value={`${fmt(preview.net_usdt)} USDT`} mono big highlight />
                {isAdmin && preview.exchange_profit_usdt !== undefined && (
                  <>
                    <div className="h-px bg-slate-700/50 my-2" />
                    <Row label="💹 汇率利润" value={`${fmt(preview.exchange_profit_usdt)} USDT`} mono profit />
                    <Row label="🧾 手续费利润" value={`${fmt(preview.fee_amount_usdt)} USDT`} mono profit />
                    <Row label="✨ 本单利润" value={`${fmt(preview.total_profit_usdt)} USDT`} mono profit big />
                  </>
                )}
              </>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5 mt-4">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button onClick={() => setStep(1)} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="back-btn">
              <ArrowLeft size={16} /> 返回修改
            </button>
            <button onClick={submit} disabled={loading} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="confirm-submit-btn">
              <CheckCircle size={16} weight="duotone" /> {loading ? '提交中…' : '确认提交'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Success */}
      {step === 3 && (
        <div className="card-base p-8 sm:p-10 text-center" data-testid="order-success">
          <div className="inline-flex w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 items-center justify-center mb-4">
            <CheckCircle size={36} weight="duotone" className="text-emerald-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">订单创建成功 ✓</h2>
          <p className="text-sm text-slate-500 mt-1 font-num">订单号:{orderId.slice(0, 8)}</p>

          <div className="flex flex-col sm:flex-row gap-3 mt-6 max-w-sm mx-auto">
            <button onClick={reset} className="btn-primary flex-1" data-testid="new-order-again-btn">录下一单</button>
            <button onClick={() => navigate('/orders')} className="btn-secondary flex-1" data-testid="view-orders-btn">查看订单</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono, big, highlight, profit }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-slate-400">{label}</span>
      <span className={`${mono ? 'font-num' : ''} ${big ? 'text-xl font-semibold' : 'text-sm'} ${
        highlight ? 'text-indigo-300' : profit ? 'text-emerald-400' : 'text-white'
      } truncate`}>{value}</span>
    </div>
  );
}
